// src/pages/Officer/components/AlertStream.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  IconButton,
  alpha,
  CircularProgress,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  Notifications,
  ExpandMore,
  ExpandLess,
  TrendingUp,
  TrendingDown,
  Circle,
  RadioButtonChecked,
  CheckCircle,
  AccessTime,
  OpenInNew,
} from '@mui/icons-material';

/**
 * AlertItem - Individual alert display
 */
const AlertItem = ({ alert, onClick }) => {
  const [expanded, setExpanded] = useState(false);

  const handleExpand = (e) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  // Determine alert type and styling
  const getAlertConfig = () => {
    const type = alert.type || 'SIGNAL';
    
    switch (type.toUpperCase()) {
      case 'ENTRY':
        return {
          color: '#00ff88',
          bgColor: alpha('#00ff88', 0.15),
          borderColor: alpha('#00ff88', 0.3),
          icon: <CheckCircle sx={{ fontSize: 18 }} />,
          label: 'ENTRY',
        };
      case 'LONG':
        return {
          color: '#00ff88',
          bgColor: alpha('#00ff88', 0.1),
          borderColor: alpha('#00ff88', 0.2),
          icon: <TrendingUp sx={{ fontSize: 18 }} />,
          label: 'LONG',
        };
      case 'SHORT':
        return {
          color: '#ff4444',
          bgColor: alpha('#ff4444', 0.1),
          borderColor: alpha('#ff4444', 0.2),
          icon: <TrendingDown sx={{ fontSize: 18 }} />,
          label: 'SHORT',
        };
      default:
        return {
          color: '#00d4ff',
          bgColor: alpha('#00d4ff', 0.1),
          borderColor: alpha('#00d4ff', 0.2),
          icon: <RadioButtonChecked sx={{ fontSize: 18 }} />,
          label: 'SIGNAL',
        };
    }
  };

  const config = getAlertConfig();
  const symbol = alert.symbol || 'N/A';
  const strategy = alert.strategy || alert.strategy_id;
  const conditionName = alert.condition_name || 'Unknown';
  const time = alert.time || 'Just now';

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        bgcolor: alpha('#0f1419', 0.6),
        border: '1px solid',
        borderColor: alpha(config.borderColor, 0.5),
        borderRadius: 2,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: config.color,
          bgcolor: alpha('#0f1419', 0.8),
          transform: 'translateY(-2px)',
          boxShadow: `0 4px 12px ${alpha(config.color, 0.2)}`,
        },
      }}
    >
      {/* Main Content */}
      <Box sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          {/* Icon */}
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              bgcolor: config.bgColor,
              border: '1px solid',
              borderColor: config.borderColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: config.color,
              flexShrink: 0,
            }}
          >
            {config.icon}
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: 'grey.50',
                  fontFamily: 'monospace',
                }}
              >
                {symbol}
              </Typography>
              <Circle sx={{ fontSize: 4, color: 'grey.700' }} />
              <Typography
                variant="caption"
                sx={{
                  color: 'grey.600',
                  fontFamily: 'monospace',
                  fontSize: '0.7rem',
                }}
              >
                {strategy}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Chip
                label={config.label}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  bgcolor: config.bgColor,
                  color: config.color,
                  border: '1px solid',
                  borderColor: config.borderColor,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: 'grey.500',
                  fontSize: '0.7rem',
                }}
              >
                {conditionName}
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={0.5}>
              <AccessTime sx={{ fontSize: 12, color: 'grey.700' }} />
              <Typography
                variant="caption"
                sx={{
                  color: 'grey.700',
                  fontFamily: 'monospace',
                  fontSize: '0.65rem',
                }}
              >
                {time}
              </Typography>
              {alert.status && (
                <>
                  <Circle sx={{ fontSize: 4, color: 'grey.800', mx: 0.5 }} />
                  <Chip
                    label={alert.status}
                    size="small"
                    sx={{
                      height: 16,
                      fontSize: '0.6rem',
                      fontWeight: 600,
                      bgcolor:
                        alert.status === 'executed'
                          ? alpha('#00ff88', 0.1)
                          : alpha('#ffaa00', 0.1),
                      color: alert.status === 'executed' ? '#00ff88' : '#ffaa00',
                    }}
                  />
                </>
              )}
            </Stack>
          </Box>

          {/* Expand/View Button */}
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="View details">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onClick();
                }}
                sx={{
                  color: config.color,
                  '&:hover': {
                    bgcolor: alpha(config.color, 0.1),
                  },
                }}
              >
                <OpenInNew sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            {alert.signal_data && Object.keys(alert.signal_data).length > 0 && (
              <IconButton
                size="small"
                onClick={handleExpand}
                sx={{
                  color: 'grey.600',
                  '&:hover': {
                    bgcolor: alpha('#666', 0.1),
                  },
                }}
              >
                {expanded ? (
                  <ExpandLess sx={{ fontSize: 16 }} />
                ) : (
                  <ExpandMore sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Expanded Details */}
      {alert.signal_data && Object.keys(alert.signal_data).length > 0 && (
        <Collapse in={expanded}>
          <Box
            sx={{
              px: 2,
              pb: 2,
              pt: 0,
            }}
          >
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: alpha('#000', 0.3),
                border: '1px solid',
                borderColor: alpha('#666', 0.1),
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'grey.600',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  fontSize: '0.6rem',
                  display: 'block',
                  mb: 1,
                }}
              >
                Signal Data
              </Typography>
              <Stack spacing={0.5}>
                {Object.entries(alert.signal_data).map(([key, value]) => {
                  // Skip large nested objects for cleaner display
                  if (typeof value === 'object' && value !== null) {
                    if (Object.keys(value).length === 0) return null;
                  }

                  return (
                    <Stack
                      key={key}
                      direction="row"
                      spacing={1}
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.7rem',
                      }}
                    >
                      <Typography sx={{ color: 'grey.600', minWidth: 100 }}>
                        {key}:
                      </Typography>
                      <Typography
                        sx={{
                          color: config.color,
                          flex: 1,
                          wordBreak: 'break-word',
                        }}
                      >
                        {typeof value === 'object'
                          ? JSON.stringify(value, null, 2)
                          : String(value)}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          </Box>
        </Collapse>
      )}
    </Paper>
  );
};

/**
 * AlertStream - Main component
 */
const AlertStream = ({ alerts = [], loading = false }) => {
  const navigate = useNavigate();

  const handleAlertClick = (alert) => {
    if (alert.id) {
      navigate(`/officer/alerts/${alert.id}`);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        height: 600,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: alpha('#0a0e14', 0.8),
        border: '1px solid',
        borderColor: alpha('#00d4ff', 0.1),
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: alpha('#00d4ff', 0.1),
          bgcolor: alpha('#0f1419', 0.6),
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                bgcolor: alpha('#00d4ff', 0.1),
                border: '1px solid',
                borderColor: alpha('#00d4ff', 0.2),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Notifications sx={{ color: '#00d4ff', fontSize: 18 }} />
            </Box>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: 'grey.50' }}
              >
                Alert Stream
              </Typography>
              <Typography variant="caption" sx={{ color: 'grey.600' }}>
                {alerts.length} {alerts.length === 1 ? 'alert' : 'alerts'}
              </Typography>
            </Box>
          </Stack>

          {loading && (
            <CircularProgress size={20} thickness={4} sx={{ color: '#00d4ff' }} />
          )}
        </Stack>
      </Box>

      {/* Alerts List */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: alpha('#000', 0.2),
            borderRadius: 1,
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: alpha('#00d4ff', 0.3),
            borderRadius: 1,
            '&:hover': {
              bgcolor: alpha('#00d4ff', 0.5),
            },
          },
        }}
      >
        {loading && alerts.length === 0 ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={40} thickness={4} sx={{ color: '#00d4ff', mb: 2 }} />
            <Typography variant="body2" sx={{ color: 'grey.600' }}>
              Loading alerts...
            </Typography>
          </Box>
        ) : alerts.length === 0 ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Notifications sx={{ fontSize: 48, color: 'grey.800', mb: 2 }} />
            <Typography
              variant="body2"
              sx={{ color: 'grey.600', fontWeight: 600 }}
            >
              No alerts yet
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'grey.700',
                textAlign: 'center',
                maxWidth: 280,
                mt: 1,
              }}
            >
              Alerts will appear here when conditions are met
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {alerts.map((alert, index) => (
              <AlertItem
                key={alert.id || `alert-${index}`}
                alert={alert}
                onClick={() => handleAlertClick(alert)}
              />
            ))}
          </Stack>
        )}
      </Box>
    </Paper>
  );
};

export default AlertStream;
